import { useState } from "react";
import { useAdminCampaignPackages } from "@/hooks/useAdminCampaigns";
import { CampaignPackage } from "@/hooks/useActiveCampaign";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, RefreshCw } from "lucide-react";
import CampaignPackageForm from "./CampaignPackageForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface CampaignPackagesListProps {
  campaignId: string;
}

const CampaignPackagesList = ({ campaignId }: CampaignPackagesListProps) => {
  const { packages, isLoading, deletePackage } = useAdminCampaignPackages(campaignId);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CampaignPackage | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleEdit = (pkg: CampaignPackage) => {
    setEditingPackage(pkg);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (deletingId) {
      await deletePackage.mutateAsync(deletingId);
      setDeletingId(null);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingPackage(null);
  };

  if (showForm) {
    return (
      <CampaignPackageForm
        campaignId={campaignId}
        package_={editingPackage}
        onClose={handleCloseForm}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Packages</h3>
        <Button size="sm" onClick={() => setShowForm(true)} className="gap-1">
          <Plus className="h-3 w-3" />
          Add Package
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-4 text-muted-foreground text-sm">
          Loading...
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground text-sm">
          No packages yet. Add your first package.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Credits</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {packages.map((pkg) => (
              <TableRow key={pkg.id}>
                <TableCell>{pkg.display_order}</TableCell>
                <TableCell>
                  {pkg.package_type === 'trial_subscription' ? (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <RefreshCw className="h-3 w-3" />
                      Subscription
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">One-Off</Badge>
                  )}
                  {pkg.partner_code_filter && (
                    <Badge variant="outline" className="text-xs ml-1 text-muted-foreground">
                      🔗 {pkg.partner_code_filter}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {pkg.name}
                  {pkg.first_time_only && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      New Only
                    </Badge>
                  )}
                  {pkg.includes_nimi_plus && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Nimi+
                    </Badge>
                  )}
                </TableCell>
                <TableCell>£{pkg.price}</TableCell>
                <TableCell>{pkg.credits_amount}</TableCell>
                <TableCell>
                  {pkg.discount_percent ? `${pkg.discount_percent}%` : "-"}
                </TableCell>
                <TableCell>
                  {pkg.is_active ? (
                    <Badge className="bg-success text-success-foreground">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Disabled</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(pkg)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeletingId(pkg.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Package?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this package. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignPackagesList;
